import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Subscription, forkJoin, interval } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PageHeaderComponent } from '../../../components/page-header/page-header.component';
import { StatusKind, StatusPillComponent } from '../../../components/status-pill/status-pill.component';
import { ProjectNavComponent } from '../../../projects/project-nav/project-nav.component';
import { ProjectRoleService } from '../../../services/project-role.service';
import { ObjectMappingService } from '../../../services/object-mapping.service';
import { ObjectCaptureService } from '../../../services/object-capture.service';
import { RecordingFeedService } from '../../../services/recording-feed.service';
import {
  CancelFailureKey,
  ExecutionJob,
  ExecutionJobService,
  cancelFailureKey,
} from '../../../services/execution-job.service';
import { CaptureLocator, CaptureView, RecordingFeedEvent } from '../../../models/object-capture.model';
import { CapturePill, defaultName, isPassword, pillOf } from '../capture-rows';

type LoadState = 'loading' | 'ok' | 'notFound' | 'error';

/** Abschnitt der Aufnahme; FAILED kommt bei Aufnahmen nicht vor und gilt wie beendet. */
type Phase = 'QUEUED' | 'RUNNING' | 'ENDED' | 'ERROR';

const PHASE_PILL: Record<Phase, StatusKind> = {
  QUEUED: 'pending',
  RUNNING: 'laufend',
  ENDED: 'inaktiv',
  ERROR: 'fehler',
};

/** Server order first; live entries the server list does not contain yet stay at the end. */
function mergeById(server: CaptureView[], known: CaptureView[]): CaptureView[] {
  const ids = new Set(server.map((c) => c.id));
  return [...server, ...known.filter((c) => !ids.has(c.id))];
}

interface CaptureRow {
  id: string;
  /** Vorschlag oder Rolle mit zugaenglichem Namen; null, wenn der Text aus der Uebersetzung kommt. */
  name: string | null;
  nameKey: 'passwordField' | 'unnamed' | null;
  best: CaptureLocator | null;
  pill: CapturePill;
  /** Passwortfelder zeigen nie ein Bild, auch wenn der Runner eins mitschickt. */
  showImage: boolean;
}

function phaseOf(job: ExecutionJob): Phase {
  switch (job.status) {
    case 'QUEUED':
      return 'QUEUED';
    case 'RUNNING':
      return 'RUNNING';
    case 'ERROR':
      return 'ERROR';
    default:
      return 'ENDED';
  }
}

const PHASE_RANK: Record<Phase, number> = { QUEUED: 0, RUNNING: 1, ENDED: 2, ERROR: 2 };

/** Leer-Hinweis je Engine; ohne Eintrag (Browser, unbekannt) gilt der Browser-Hinweis. */
const EMPTY_HINTS: Readonly<Record<string, string>> = {
  windows: 'objectMappings.recording.emptyScanWindows',
  sap: 'objectMappings.recording.emptyScanSap',
};

/** A reload may answer after a newer live event; it must not move the job back, and a finished job stays finished. */
function newer(current: ExecutionJob | null, loaded: ExecutionJob): ExecutionJob {
  if (!current) return loaded;
  const rank = PHASE_RANK[phaseOf(current)];
  return rank === 2 || rank > PHASE_RANK[phaseOf(loaded)] ? current : loaded;
}

function toRow(capture: CaptureView): CaptureRow {
  const name = defaultName(capture);
  return {
    id: capture.id,
    name,
    nameKey: name ? null : isPassword(capture) ? 'passwordField' : 'unnamed',
    best: capture.locators[0] ?? null,
    pill: pillOf(capture.locators),
    showImage: capture.hasImage && !isPassword(capture),
  };
}

function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Seite „Aufnahme läuft“ (NEX-1718): zeigt jede Aufnahme aus dem Runner-Browser live.
 *
 * Design-Luecke: Das Bundle kennt keine Aufnahme. Kopf und Fehlerflaeche folgen der Live-Karte aus
 * page-testlauf.jsx (444-516), Liste und Karten dem Stil der Objektbibliothek.
 */
@Component({
  selector: 'app-recording-live',
  imports: [
    TranslatePipe,
    RouterLink,
    MatButton,
    MatIcon,
    MatProgressSpinner,
    PageHeaderComponent,
    StatusPillComponent,
    ProjectNavComponent,
  ],
  templateUrl: './recording-live.component.html',
  styleUrl: './recording-live.component.scss',
  providers: [RecordingFeedService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecordingLiveComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly jobs = inject(ExecutionJobService);
  private readonly captureService = inject(ObjectCaptureService);
  private readonly mappings = inject(ObjectMappingService);
  private readonly feed = inject(RecordingFeedService);

  readonly isReadOnly = inject(ProjectRoleService).isReadOnly;

  readonly projectId: string = this.route.snapshot.params['projectId'];
  readonly mappingId: string = this.route.snapshot.params['mappingId'];
  readonly jobId: string = this.route.snapshot.params['jobId'];

  readonly libraryName = signal<string | null>(null);
  /** Windows und SAP scannen das Fenster im Vordergrund (NEX-2066); der Browser-Hinweis passt dort nicht. */
  readonly libraryEngine = signal<string | null>(null);
  readonly emptyHint = computed(() => EMPTY_HINTS[this.libraryEngine() ?? ''] ?? 'objectMappings.recording.empty');
  readonly state = signal<LoadState>('loading');
  readonly job = signal<ExecutionJob | null>(null);
  readonly stopping = signal(false);
  readonly stopNotice = signal<CancelFailureKey | null>(null);
  readonly connectionLost = computed(() => this.feed.connection() === 'lost');

  private readonly captures = signal<CaptureView[]>([]);
  private readonly imageUrls = signal<ReadonlyMap<string, string>>(new Map());
  private readonly now = signal(Date.now());
  private readonly requestedImages = new Set<string>();
  private loadRequest: Subscription | null = null;

  readonly phase = computed<Phase | null>(() => {
    const job = this.job();
    return job ? phaseOf(job) : null;
  });
  readonly statusPill = computed(() => PHASE_PILL[this.phase() ?? 'QUEUED']);
  readonly active = computed(() => this.phase() === 'QUEUED' || this.phase() === 'RUNNING');
  readonly finished = computed(() => this.phase() === 'ENDED' || this.phase() === 'ERROR');
  readonly canStop = computed(() => this.active() && !this.isReadOnly());
  readonly cancelRequested = computed(() => this.active() && !!this.job()?.cancelRequested);
  readonly rows = computed(() => this.captures().map(toRow));
  readonly counts = computed(() => {
    const rows = this.rows();
    return {
      captured: rows.length,
      stable: rows.filter((r) => r.pill === 'stable').length,
      positionOnly: rows.filter((r) => r.pill === 'positionOnly').length,
      nameTaken: this.captures().filter((c) => c.nameTaken).length,
    };
  });
  readonly runtime = computed(() => {
    const job = this.job();
    if (!job?.startedAt) return null;
    const end = job.finishedAt ? new Date(job.finishedAt).getTime() : this.now();
    return formatDuration(end - new Date(job.startedAt).getTime());
  });
  readonly reviewLink = computed(() => ['/projects', this.projectId, 'object-mappings', this.mappingId, 'review']);

  constructor() {
    // Events between the initial GET and the subscription would be lost, so every (re)connect re-syncs.
    let previous = this.feed.connection();
    effect(() => {
      const current = this.feed.connection();
      const connected = previous !== 'connected' && current === 'connected';
      previous = current;
      if (connected) untracked(() => this.load());
    });
    interval(1000)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (this.phase() === 'RUNNING') this.now.set(Date.now());
      });
    this.feed.events.pipe(takeUntilDestroyed()).subscribe((event) => this.onEvent(event));
    this.destroyRef.onDestroy(() => {
      this.loadRequest?.unsubscribe();
      this.feed.disconnect();
      this.imageUrls().forEach((url) => URL.revokeObjectURL(url));
    });
  }

  ngOnInit(): void {
    this.mappings
      .listAll(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (all) => {
          const library = all.find((lib) => lib.id === this.mappingId);
          this.libraryName.set(library?.name ?? null);
          this.libraryEngine.set(library?.engineId ?? null);
        },
        error: () => this.libraryName.set(null),
      });
    this.load();
  }

  imageUrl(id: string): string | null {
    return this.imageUrls().get(id) ?? null;
  }

  retry(): void {
    this.state.set('loading');
    this.load();
  }

  stop(): void {
    if (this.stopping()) return;
    this.stopping.set(true);
    this.stopNotice.set(null);
    this.jobs
      .cancel(this.projectId, this.jobId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (job) => {
          this.job.set(job);
          this.stopping.set(false);
        },
        error: (err: unknown) => {
          const key = cancelFailureKey(err);
          this.stopNotice.set(key);
          this.stopping.set(false);
          if (key === 'alreadyFinished') this.reloadJob();
        },
      });
  }

  /** Erstes Laden, „Erneut versuchen“ und Abgleich nach jedem Verbindungsaufbau. */
  private load(): void {
    this.loadRequest?.unsubscribe();
    this.loadRequest = forkJoin([
      this.jobs.get(this.projectId, this.jobId),
      this.captureService.list(this.projectId, this.mappingId),
    ]).subscribe({
      next: ([job, captures]) => {
        if (job.kind !== 'RECORDING' || job.objectMappingId !== this.mappingId) {
          this.state.set('notFound');
          return;
        }
        this.job.update((current) => newer(current, job));
        this.captures.update((known) =>
          mergeById(
            captures.filter((c) => c.jobId === this.jobId),
            known,
          ),
        );
        this.captures().forEach((c) => this.loadImage(c));
        this.state.set('ok');
        this.feed.connect(this.projectId, this.mappingId);
      },
      error: (err: unknown) => {
        const missing = err instanceof HttpErrorResponse && (err.status === 404 || err.status === 400);
        this.state.set(missing ? 'notFound' : 'error');
      },
    });
  }

  /** Die Nachricht traegt weder Rechner noch Zeiten; die kommen aus dem Auftrag. */
  private reloadJob(): void {
    this.jobs
      .get(this.projectId, this.jobId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (job) => this.job.set(job), error: () => undefined });
  }

  private onEvent(event: RecordingFeedEvent): void {
    if (event.type === 'CAPTURE') {
      const capture = event.capture;
      if (capture.jobId !== this.jobId || this.captures().some((c) => c.id === capture.id)) return;
      this.captures.update((list) => [...list, capture]);
      this.loadImage(capture);
      return;
    }
    if (event.jobId !== this.jobId) return;
    this.job.update((job) => (job ? { ...job, status: event.status, error: event.error } : job));
    this.reloadJob();
  }

  private loadImage(capture: CaptureView): void {
    if (!toRow(capture).showImage || this.requestedImages.has(capture.id)) return;
    this.requestedImages.add(capture.id);
    this.captureService
      .image(this.projectId, this.mappingId, capture.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          this.imageUrls.update((urls) => new Map(urls).set(capture.id, url));
        },
        error: () => undefined,
      });
  }
}
